import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Box, Button, Typography } from "@mui/material";
import QueryViewer from "components/queries/QueryViewer";
import DashboardSelectionButton from "components/resources/dashboards/DashboardSelectionButton";
import { Query, socialDatasetId } from "system/query/Query";
import { useTranslation } from "react-i18next";
import APIRequest, { APIError } from "system/APIRequest";
import { QueryInfo } from "system/query/QueryInfo";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

export interface AIQueryResultProps {
  queryResult: Query | undefined;
  savedQueryId: string | undefined;
  setSavedQueryId: (id: string | undefined) => void;
}

export function AIQueryResult(props: AIQueryResultProps) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const { queryResult, savedQueryId, setSavedQueryId } = props;

  const [destinationDashboardId, setDestinationDashboardId] = useState<string | undefined>(undefined);
  const [queryInfo, setQueryInfo] = useState<QueryInfo | undefined>(undefined);

  useEffect(() => {
    APIRequest(`query/info`, {}, {
      method: 'POST',
      body: JSON.stringify({
        "dataset_id": socialDatasetId
      }),
    }).then((response) => {
      setQueryInfo(new QueryInfo(response));
    }).catch((error) => {
      console.error(`Error loading query info: ${error}`);
      Sentry.captureException(error);
    });
  }, []);

  const saveQuery = (isEphemeral: boolean, callback: (savedQueryId:string) => void) => {
    if (!queryResult) {
      return;
    }
    setSavedQueryId(undefined);
    const query = new Query({...queryResult});
    if (isEphemeral) {
      query.is_ephemeral = true;
    }

    // Limit the name length to 256 characters,
    // which is the maximum length for query names via the API.
    if (query?.name?.length >= 256) {
      query.name = query.name.substring(0, 256);
    }

    APIRequest("obj/query", {}, {
      method: "POST",
      body: JSON.stringify(query, undefined, 2),
    }).then((response) => {
      setSavedQueryId(response.id);
      callback(response.id);
    }).catch((error: APIError) => {
      console.error(`Error saving query: ${error}`);
      Sentry.captureException(error);
      enqueueSnackbar(`${error.message}`, {variant: 'error', persist: true});
    });
  }

  return (
    <Box>
      {queryResult && queryInfo &&
        <>
          <Typography variant="h4" sx={{mb: 0}}>
            {i18n.t("ai-query.generated-query")}:
          </Typography>
          <QueryViewer query={queryResult} queryInfo={queryInfo} />
        </>
      }
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", mt: 2 }}>
        <Box>
          <DashboardSelectionButton
            limitToTemplateDashboards={true}
            selectedDashboardId={destinationDashboardId || ""}
            setSelectedDashboardId={(dashboardId:string | undefined) => {
              setDestinationDashboardId(dashboardId);
            }}
          />
          <Button
            disabled={!destinationDashboardId}
            onClick={() => {
              // if the query is not saved, save it and navigate to the dashboard
              if (!savedQueryId) {
                saveQuery(false, (responseQueryId) => {
                  navigate(`/dashboard/${destinationDashboardId}/${responseQueryId}`);
                });
              } else {
                // Otherwise, navigate to the dashboard using the saved query id
                navigate(`/dashboard/${destinationDashboardId}/${savedQueryId}`);
              }
            }}
            color="primary">
            {i18n.t("drilldown")}
          </Button>
        </Box>
        <Box sx={{textAlign: "right"}}>
          <Button
            variant="contained"
            disabled={!!savedQueryId}
            onClick={() => {
              // Save query
              saveQuery(false, () => {
                enqueueSnackbar(i18n.t("ai-query.saved-query"), {variant: 'success'});
              });
            }}>
            {savedQueryId ? i18n.t("ai-query.saved-query") : i18n.t("ai-query.save-query")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}